/**
 * Performance Callback Registry - Solves Multiple Hook Callback Conflicts
 * 
 * This registry allows multiple hooks to subscribe to performance updates
 * from LocalDocumentEngine without overwriting each other's callbacks.
 */

import React from 'react';
import type { PerformanceMetrics } from '../types/DocumentTypes';
import type { LocalDocumentEngine } from '../core/LocalDocumentEngine';

export type PerformanceCallback = (metrics: PerformanceMetrics) => void;
export type DocumentChangeCallback = (state: any) => void;

/**
 * Registry for managing multiple performance callbacks
 */
class PerformanceCallbackRegistry {
  private performanceCallbacks = new Map<string, PerformanceCallback>();
  private documentChangeCallbacks = new Map<string, DocumentChangeCallback>();
  private documentEngine: LocalDocumentEngine | null = null;
  private isInitialized = false;

  /**
   * Initialize the registry with a document engine
   */
  initialize(documentEngine: LocalDocumentEngine): void {
    if (this.isInitialized && this.documentEngine === documentEngine) {
      return; // Already initialized with same engine
    }

    this.documentEngine = documentEngine;
    this.isInitialized = true;

    // Set up the unified performance callback
    this.documentEngine.setPerformanceUpdateCallback((metrics: PerformanceMetrics) => {
      this.notifyPerformanceCallbacks(metrics);
    });

    // Set up the unified document change callback
    this.documentEngine.setDocumentChangeCallback((state: any) => {
      this.notifyDocumentChangeCallbacks(state);
    });

    console.log('[PerformanceCallbackRegistry] ✅ Initialized with unified callbacks');
  }

  /**
   * Subscribe to performance updates
   */
  subscribeToPerformance(hookId: string, callback: PerformanceCallback): () => void {
    if (!this.isInitialized) {
      throw new Error('Registry must be initialized before subscribing');
    }

    this.performanceCallbacks.set(hookId, callback);
    console.log(`[PerformanceCallbackRegistry] 📊 Performance subscription added: ${hookId} (total: ${this.performanceCallbacks.size})`);

    // Return unsubscribe function
    return () => {
      this.performanceCallbacks.delete(hookId);
      console.log(`[PerformanceCallbackRegistry] 🗑️ Performance subscription removed: ${hookId} (remaining: ${this.performanceCallbacks.size})`);
    };
  }

  /**
   * Subscribe to document change updates
   */
  subscribeToDocumentChange(hookId: string, callback: DocumentChangeCallback): () => void {
    if (!this.isInitialized) {
      throw new Error('Registry must be initialized before subscribing');
    }

    this.documentChangeCallbacks.set(hookId, callback);
    console.log(`[PerformanceCallbackRegistry] 📝 Document change subscription added: ${hookId} (total: ${this.documentChangeCallbacks.size})`);

    // Return unsubscribe function
    return () => {
      this.documentChangeCallbacks.delete(hookId);
      console.log(`[PerformanceCallbackRegistry] 🗑️ Document change subscription removed: ${hookId} (remaining: ${this.documentChangeCallbacks.size})`);
    };
  }

  /**
   * Notify all performance callbacks
   */
  private notifyPerformanceCallbacks(metrics: PerformanceMetrics): void {
    this.performanceCallbacks.forEach((callback, hookId) => {
      try {
        callback(metrics);
      } catch (error) {
        console.error(`[PerformanceCallbackRegistry] ❌ Error in performance callback for ${hookId}:`, error);
      }
    });
  }

  /**
   * Notify all document change callbacks
   */
  private notifyDocumentChangeCallbacks(state: any): void {
    this.documentChangeCallbacks.forEach((callback, hookId) => {
      try {
        callback(state);
      } catch (error) {
        console.error(`[PerformanceCallbackRegistry] ❌ Error in document change callback for ${hookId}:`, error);
      }
    });
  }

  /**
   * Get registry statistics
   */
  getStats() {
    return {
      performanceSubscribers: this.performanceCallbacks.size,
      documentChangeSubscribers: this.documentChangeCallbacks.size,
      isInitialized: this.isInitialized,
      engineConnected: !!this.documentEngine
    };
  }

  /**
   * Cleanup all subscriptions
   */
  cleanup(): void {
    if (this.documentEngine) {
      // Clear the engine callbacks
      this.documentEngine.setPerformanceUpdateCallback(() => {});
      this.documentEngine.setDocumentChangeCallback(() => {});
    }

    this.performanceCallbacks.clear();
    this.documentChangeCallbacks.clear();
    this.documentEngine = null;
    this.isInitialized = false;

    console.log('[PerformanceCallbackRegistry] 🧹 Cleanup completed');
  }
}

// Singleton instance
const performanceCallbackRegistry = new PerformanceCallbackRegistry();

/**
 * Get the performance callback registry instance
 * This is not a React hook - just a utility function
 */
export function getPerformanceCallbackRegistry(documentEngine: LocalDocumentEngine) {
  // Initialize registry if needed
  if (documentEngine) {
    performanceCallbackRegistry.initialize(documentEngine);
  }

  return performanceCallbackRegistry;
}

/**
 * React hook for using the performance callback registry
 * Properly follows React hook rules
 */
export function usePerformanceCallbackRegistry(documentEngine: LocalDocumentEngine) {
  // Always call this hook - no conditional logic
  const registry = getPerformanceCallbackRegistry(documentEngine);
  
  // Use useEffect to handle initialization
  React.useEffect(() => {
    if (documentEngine) {
      registry.initialize(documentEngine);
    }
  }, [documentEngine, registry]);

  return registry;
}

/**
 * Utility hook for performance subscriptions
 */
export function usePerformanceSubscription(
  hookId: string,
  documentEngine: LocalDocumentEngine,
  callback: PerformanceCallback
): () => void {
  const registry = usePerformanceCallbackRegistry(documentEngine);

  // Use React useEffect equivalent (this would be wrapped in actual useEffect in real usage)
  const unsubscribe = registry.subscribeToPerformance(hookId, callback);

  // Return cleanup function (would be returned from useEffect)
  return unsubscribe;
}

/**
 * Utility hook for document change subscriptions
 */
export function useDocumentChangeSubscription(
  hookId: string,
  documentEngine: LocalDocumentEngine,
  callback: DocumentChangeCallback
): () => void {
  const registry = usePerformanceCallbackRegistry(documentEngine);

  // Use React useEffect equivalent
  const unsubscribe = registry.subscribeToDocumentChange(hookId, callback);

  // Return cleanup function
  return unsubscribe;
}

export { performanceCallbackRegistry };
